import React, { memo, useMemo } from 'react';

import { CheckoutConfirmationResponseType, ParticipantTypeCode } from 'src/models/Checkout/Confirmation';
import { useTHelper } from 'src/utils/i18n';
import { CheckoutConfirmationTable } from 'src/pages/Checkout/components/common/Table';
import { commonFormatCurrencyWithType } from 'src/utils/currency';
import { UiCard } from 'src/components/UI/Card';
import { useActiveModal } from 'src/utils/hooks/useActiveModal';
import { UiButton } from 'src/components/UI/Button';
import { checkModalData } from 'src/utils/modal';
import { InfoIcon } from 'src/components/Icons/Info';
import styles from './styles.module.scss';
import { CreditingParticipantsInfoModal } from './CreditingParticipantsInfoModal';

type Props = {
  model: CheckoutConfirmationResponseType['TeamMemberPaymentsSubSection'];
};

type ModalsType = 'creditingInfo';
type ListItemType = CheckoutConfirmationResponseType['TeamMemberPaymentsSubSection'];

export const CheckoutConfirmationTeamMemberPayments = memo<Props>(({ model }) => {
  const tHelper = useTHelper('checkout.confirmation.teamMemberPayments');

  const anyActivityPayments = useMemo(
    () => model.Items.some(({ ActivityPayments }) => ActivityPayments.some(({ AthonActivityID }) => AthonActivityID)),
    [model.Items]
  );
  const anyMemberPayment = useMemo(() => model.Items.some((item) => item.IsMemberPayment), [model.Items]);
  const anyTeams = useMemo(
    () => model.Items.some((item) => item.TeamID || item.TeamWriteInCredit !== null),
    [model.Items]
  );

  const { activeModal, openModal, closeModal } = useActiveModal<ModalsType, ListItemType | null>();

  const teamMemberSingular = model.TeamMemberTerminology.Singular || '';
  const teamSingular = model.TeamTerminology.Singular || '';

  const columns = [
    anyMemberPayment && { name: teamMemberSingular, className: 'hidden-xs' },
    anyTeams && { name: teamSingular, className: 'hidden-xs' },
    anyActivityPayments && { name: tHelper('activity'), className: 'hidden-xs' },
    {
      name: `${anyMemberPayment ? teamMemberSingular : ''}${anyMemberPayment && anyTeams ? ' / ' : ''}${
        anyTeams ? teamSingular : ''
      }`,
      className: 'visible-xs',
    },
    model.IsPledge && { name: tHelper('pledge'), className: 'right' },
    !model.IsPledge && model.Items.length > 1 && { name: tHelper('credit'), className: 'right' },
  ];

  return (
    <>
      <h2 className="section-header">{model.SectionHeader}</h2>
      <CheckoutConfirmationTable columns={columns} data={model.Items} customRow>
        {(item) => (
          <>
            {item.ActivityPayments.length ? (
              item.ActivityPayments.filter((payment) => payment.AmountPerUnit || payment.Amount).map((payment) => (
                <tr key={payment.AthonActivityID}>
                  {anyMemberPayment && (
                    <td className="hidden-xs">{item.TeamMemberName || item.TeamMemberWriteInCredit}</td>
                  )}
                  {anyTeams && (
                    <td className="hidden-xs">
                      {item.TeamName ||
                        item.TeamWriteInCredit ||
                        tHelper('no', { singular: model.TeamTerminology.Singular || '' })}
                    </td>
                  )}
                  {anyActivityPayments && <td className="hidden-xs">{payment.ActivityTitle}</td>}

                  <td className="visible-xs">
                    <div>{item.TeamMemberName || item.TeamMemberWriteInCredit}</div>
                    {anyTeams && (
                      <div>
                        {item.TeamName ||
                          item.TeamWriteInCredit ||
                          tHelper('no', { singular: model.TeamTerminology.Singular || '' })}
                      </div>
                    )}
                  </td>

                  <td className="right">
                    {(item.IsMemberPayment || item.IsTeamPayment) &&
                      !!payment.Amount &&
                      !payment.AmountPerUnit &&
                      model.IsPledge &&
                      commonFormatCurrencyWithType(payment.Amount, model.CurrencyType)}

                    {(item.TeamMemberName || item.TeamMemberWriteInCredit) && !!payment.AmountPerUnit && (
                      <>
                        {tHelper('per', {
                          amount: commonFormatCurrencyWithType(payment.AmountPerUnit, model.CurrencyType) as string,
                          singular: payment.UnitTerminologySingular.toLowerCase(),
                        })}
                        {!!payment.MaximumAmount &&
                          tHelper('upTo', {
                            amount: commonFormatCurrencyWithType(payment.MaximumAmount, model.CurrencyType) as string,
                          })}
                      </>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                {anyMemberPayment && (
                  <td className="hidden-xs">{item.TeamMemberName || item.TeamMemberWriteInCredit}</td>
                )}

                {anyTeams && (
                  <td className="hidden-xs">
                    {item.TeamName ||
                      item.TeamWriteInCredit ||
                      tHelper('no', { singular: model.TeamTerminology.Singular || '' })}
                  </td>
                )}

                {anyActivityPayments && <td className="hidden-xs" />}

                <td className="visible-xs">
                  <div>{item.TeamMemberName || item.TeamMemberWriteInCredit}</div>
                  {anyTeams && (
                    <div>
                      {item.TeamName ||
                        item.TeamWriteInCredit ||
                        tHelper('no', { singular: model.TeamTerminology.Singular || '' })}
                    </div>
                  )}
                </td>

                {(model.Items.length > 1 || model.IsPledge) && (
                  <td className="right">
                    {(item.IsMemberPayment || item.IsTeamPayment) &&
                      item.AmountPercentage >= 0 &&
                      !model.IsPledge &&
                      `${item.AmountPercentage} %`}

                    {item.Amount > 0 && model.IsPledge ? (
                      commonFormatCurrencyWithType(item.Amount, model.CurrencyType)
                    ) : (
                      <>
                        {(item.TeamMemberName || item.TeamMemberWriteInCredit) &&
                          !!item.AmountPerUnit &&
                          model.IsPledge && (
                            <>
                              {tHelper('per', {
                                amount: commonFormatCurrencyWithType(item.AmountPerUnit, model.CurrencyType) as string,
                                singular: item.UnitTerminology.Singular?.toLowerCase() || '',
                              })}
                              {!!item.MaximumAmount &&
                                tHelper('upTo', {
                                  amount: commonFormatCurrencyWithType(
                                    item.MaximumAmount,
                                    model.CurrencyType
                                  ) as string,
                                })}
                            </>
                          )}
                      </>
                    )}
                  </td>
                )}
              </tr>
            )}
          </>
        )}
      </CheckoutConfirmationTable>
      {model.HasNotAvailableCreditActivities && (
        <UiCard>
          <div className="d-flex">
            <div className={styles.iconBox}>
              <InfoIcon className="tooltip-info-icon" />
            </div>
            <div className="fr-view-custom-text">
              {tHelper('infoLabel', {
                terminologyPlural:
                  model.ParticipantTypeID === ParticipantTypeCode.Member
                    ? model.TeamMemberTerminology.Plural || 'Participants'
                    : model.TeamTerminology.Plural || 'Teams',
                amount: commonFormatCurrencyWithType(
                  model.Items.reduce((sum, item) => sum + item.Amount, 0),
                  model.CurrencyType
                ) as string,
                donationTerminology: model.DonationTerminology.Singular?.toLowerCase() || 'donation',
              })}
              <UiButton tag="a" onClick={openModal('creditingInfo', model)}>
                {tHelper('learnMore')}
              </UiButton>
            </div>
          </div>

          {checkModalData<ModalsType>(activeModal, 'creditingInfo') && (
            <CreditingParticipantsInfoModal model={model} onClose={closeModal} />
          )}
        </UiCard>
      )}
    </>
  );
});
